import { useState } from 'react';
import { isAxiosError } from 'axios';

import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/AuthContext';
import { formatInstant } from '@/lib/utils/date';
import { Checkbox } from '@/components/ui/checkbox';
import { usePermissions } from '@/features/permissions';
import { displayPayload, eventHeads } from './projection';
import type { ClinicalEvent, ClinicalSubject } from './types';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { useVisitSigning, type VisitSigningInput } from './signingApi';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

type Preview = ReturnType<typeof visitSigningPlan> & {
  encounterId: string;
  date: string;
  input: VisitSigningInput;
  userId: string;
  orgId: string;
};

export function visitSigningPlan(events: ClinicalEvent[], encounterId: string, userId: string) {
  const eligible: ClinicalEvent[] = [];
  const excluded: { event: ClinicalEvent; reason: string }[] = [];
  for (const event of eventHeads(events).filter((e) => encounterId === '__all__' || e.encounter_id === encounterId)) {
    const chain = events.filter((e) => e.root_id === event.root_id);
    const original = chain.find((e) => e.id === e.root_id && e.kind === 'create');
    const content = chain
      .filter((e) => e.kind === 'create' || e.kind === 'amend')
      .sort((a, b) => b.sequence - a.sequence)[0];
    const reason =
      event.status !== 'ACTIVE'
        ? event.status.replaceAll('_', ' ').toLowerCase()
        : event.signed
          ? 'already signed'
          : !userId || ![content?.author_id, original?.author_id].includes(userId)
            ? 'requires its author to sign'
            : null;
    if (reason) excluded.push({ event, reason });
    else eligible.push(event);
  }
  return { eligible, excluded };
}

export function VisitSignOff({
  events,
  subjects,
  timeZone,
  episodeId,
  disabledReason,
}: {
  episodeId: string;
  events: ClinicalEvent[];
  subjects: ClinicalSubject[];
  timeZone: string;
  disabledReason?: string;
}) {
  const { user, organization } = useAuth();
  const { permissions } = usePermissions();
  const signing = useVisitSigning(episodeId);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [reviewed, setReviewed] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState('');
  const [attempted, setAttempted] = useState(false);
  if (!permissions.includes('sign_clinical_event') || !user) return null;
  const visits = [...new Set(eventHeads(events).map((e) => e.encounter_id))].map((encounterId) => {
    const plan = visitSigningPlan(events, encounterId, user.id);
    const times = eventHeads(events)
      .filter((e) => e.encounter_id === encounterId)
      .map((e) => e.occurred_at)
      .sort();
    return { encounterId, ...plan, date: formatInstant(times[0], 'MMM d, yyyy', timeZone) };
  });
  if (visits.length > 1)
    visits.unshift({ date: 'all visits', encounterId: '__all__', ...visitSigningPlan(events, '__all__', user.id) });
  function open(visit: (typeof visits)[number]) {
    setPreview({
      ...visit,
      userId: user!.id,
      orgId: organization!.organization_id,
      input: {
        device_id: `visit-sign-${crypto.randomUUID()}`,
        reason: 'I reviewed and signed the listed visit observations.',
        entries: visit.eligible.map((e, index) => ({
          root_id: e.root_id,
          client_seq: index + 1,
          expected_head_id: e.id,
        })),
      },
    });
    setReviewed(false);
    setError('');
    setResult('');
    setAttempted(false);
  }
  const identityChanged = preview && (preview.userId !== user.id || preview.orgId !== organization?.organization_id);
  async function submit() {
    if (!preview || !reviewed || identityChanged || disabledReason || signing.isPending) return;
    setAttempted(true);
    setError('');
    try {
      await signing.mutateAsync({ input: preview.input, encounterId: preview.encounterId });
      setResult(`Signed ${preview.eligible.length} reviewed observations for ${preview.date}.`);
      setPreview(null);
    } catch (failure) {
      const status = isAxiosError(failure) ? failure.response?.status : undefined;
      setError(
        status === 409
          ? 'An observation changed or was signed elsewhere. Close this review, refresh the visit, and review its current entries.'
          : status === 403
            ? 'Signing permission or author access changed. Your review is retained; verify access before retrying.'
            : 'The signing result could not be confirmed. Retry this same reviewed request to safely check the result.',
      );
    }
  }
  return (
    <section aria-label="Visit signing" className="flex flex-col gap-3">
      <p>Sign off a visit after reviewing its current observations.</p>
      {disabledReason && (
        <Alert>
          <AlertDescription>{disabledReason}</AlertDescription>
        </Alert>
      )}
      {result && <p role="status">{result}</p>}
      <div className="flex flex-wrap gap-3">
        {visits.map((visit) => (
          <Button
            key={visit.encounterId}
            variant="outline"
            className="min-h-11"
            onClick={() => open(visit)}
            disabled={
              !!disabledReason ||
              !organization ||
              !visit.eligible.length ||
              visit.eligible.length > 100 ||
              !navigator.onLine
            }>
            {visit.encounterId === '__all__' ? 'Review all visits' : `Review visit ${visit.date}`} ·{' '}
            {visit.eligible.length} unsigned
          </Button>
        ))}
      </div>
      {visits.some((v) => v.eligible.length > 100) && (
        <p>Visits with more than 100 unsigned observations require individual signing.</p>
      )}
      <Dialog
        open={!!preview}
        onOpenChange={(open) => {
          if (!open && !signing.isPending) setPreview(null);
        }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Review visit {preview?.date}</DialogTitle>
            <DialogDescription>
              Sign only the observations listed below. Other authors’ entries and observations awaiting review or
              co-sign remain separate.
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Observed</TableHead>
                    <TableHead>Subject / observation</TableHead>
                    <TableHead>Finding</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.eligible.map((event) => (
                    <TableRow key={event.id}>
                      <TableCell>{formatInstant(event.occurred_at, 'MMM d, yyyy HH:mm', timeZone)}</TableCell>
                      <TableCell>
                        {subjects.find((s) => s.id === event.subject_id)?.label} · {event.event_type}
                      </TableCell>
                      <TableCell className="whitespace-pre-wrap">{displayPayload(event.payload)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {preview.excluded.length > 0 && (
                <details>
                  <summary>{preview.excluded.length} observations excluded from this signing</summary>
                  <ul>
                    {preview.excluded.map(({ event, reason }) => (
                      <li key={event.id}>
                        {event.event_type}: {reason}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              <FieldGroup>
                <Field orientation="horizontal">
                  <Checkbox
                    id="visit-sign-reviewed"
                    checked={reviewed}
                    disabled={signing.isPending || attempted}
                    onCheckedChange={(value) => setReviewed(value === true)}
                  />
                  <FieldLabel htmlFor="visit-sign-reviewed">
                    I reviewed these {preview.eligible.length} observations and confirm signing them.
                  </FieldLabel>
                </Field>
              </FieldGroup>
              {(error || identityChanged) && (
                <Alert variant="destructive">
                  <AlertDescription>
                    {identityChanged
                      ? 'The active clinician or practice changed. Close this review and start again.'
                      : error}
                  </AlertDescription>
                </Alert>
              )}
              <DialogFooter>
                <Button variant="outline" disabled={signing.isPending} onClick={() => setPreview(null)}>
                  Close review
                </Button>
                <Button
                  className="min-h-11"
                  onClick={submit}
                  disabled={
                    !reviewed || signing.isPending || !!identityChanged || !!disabledReason || !navigator.onLine
                  }>
                  {signing.isPending
                    ? 'Signing…'
                    : attempted
                      ? 'Retry reviewed signing'
                      : `Sign ${preview.eligible.length} reviewed observations`}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
